import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { IColumnConfigTableResponsive } from 'src/app/domain/IColumnConfigTableResponsive';
import { FieldTypeEnum } from 'src/app/domain/registration/enums/field-type.enum';
import { SystemDueDateEnum, SystemTypeEnum, SystemTypeIdEnum, createDueDateOptions, createSystemTypeOptions, getSystemTypeDescriptionByValue } from 'src/app/domain/registration/enums/system-type.enum';
import { S3_URL_BASE } from 'src/environments/environment';
import { PlanSelectionStep } from './plan-selection-step.enum';
import { IBranchComposition, IBranchRow, IPlanForSelection, IPlanSelectionSummary, IPlanVariationForSelection, ISystemForSelection, ISystemModuleForSelection } from './plan-selection.models';
import { PlanSelectionService } from './plan-selection.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { PlanSolutionModulesDialogComponent } from '../plan-solution-modules-dialog/plan-solution-modules-dialog.component';
import { ToastService } from 'src/app/shared/services/toast/toast.service';

@Component({
    selector: 'app-plan-selection-wizard',
    templateUrl: './plan-selection-wizard.component.html',
    styleUrls: ['./plan-selection-wizard.component.sass'],
    providers: [PlanSelectionService],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanSelectionWizardComponent implements OnInit, OnDestroy {
    @ViewChild('stepper') private stepper: MatStepper;
    @Output() selectionFinished = new EventEmitter<{ plan: IPlanForSelection, system: ISystemForSelection, modules?: ISystemModuleForSelection[], variations?: IPlanVariationForSelection[], branchCompositions?: IBranchComposition[], maxBranches?: number | null, updateReason?: string, summary?: IPlanSelectionSummary }>();

    private destroy$ = new Subject<void>();

    PlanSelectionStep = PlanSelectionStep;
    SystemTypeIdEnum = SystemTypeIdEnum;
    SystemTypeEnum = SystemTypeEnum;
    SystemDueDateEnum = SystemDueDateEnum;
    getSystemTypeDescription = getSystemTypeDescriptionByValue;
    s3UrlBase = S3_URL_BASE;

    currentStep: PlanSelectionStep = PlanSelectionStep.SELECT_PLAN;
    isLoadingPlans = false;
    isLoadingSystems = false;
    isLoadingModules = false;
    promptedForModule = false;
    isEditMode = false;

    allPlans: IPlanForSelection[] = [];
    filteredPlans: IPlanForSelection[] = [];
    availableSystems: ISystemForSelection[] = [];
    availableModules: ISystemModuleForSelection[] = [];
    availableVariations: IPlanVariationForSelection[] = [];

    // Overrides editaveis no passo de Resumo. Quando != null, o valor digitado
    // prevalece sobre o calculado a partir das variacoes/modulos.
    summaryMonthlyValueOverride: number | null = null;
    summaryAdhesionFeeOverride: number | null = null;
    summaryCostOverride: number | null = null;
    // Quantidade maxima de filiais do plano (opcional). null = sem limite.
    maxBranches: number | null = null;

    // --- Step 3 "Matriz & Filiais" e Step 4 "Limites por filial" (novos no Core) ---
    // Ver specs/nexus/plan-wizard-cliente-redesign.md.

    /** Terminais do plano — pré-preenchido do catálogo, editável (Step 1). */
    planTerminalQuantity: number | null = null;

    branchRows: IBranchRow[] = [];
    /** Filial incluída no plano (Matriz sempre true/travada). */
    branchSelected: Record<string, boolean> = {};
    /** "Mesma para todas" (false) x "Personalizar por filial" (true). */
    perFilialMode = false;
    /** Composição por filial quando perFilialMode = true. */
    branchItemSelections: Record<string, { selectedVariationIds: string[]; selectedModuleIds: string[] }> = {};
    /** Terminais/máx. usuários por filial (Step 4). */
    branchLimits: Record<string, { terminalQuantity: number | null; maxUsers: number | null }> = {};

    private branchRowsInitialized = false;

    // --- Step 5 "Revisão" — motivo da alteração (obrigatório na edição, Core) ---
    updateReason = '';

    selectedPlan: IPlanForSelection | null = null;
    selectedSystem: ISystemForSelection | null = null;
    selectedModule: ISystemModuleForSelection | null = null;
    selectedModules: ISystemModuleForSelection[] = [];
    selectedVariations: IPlanVariationForSelection[] = [];

    // Filtros
    planFilters: { systemType?: number | null; dueDate?: number | null } = {
        systemType: null,
        dueDate: null
    };

    systemTypeOptions = createSystemTypeOptions();
    dueDateOptions = createDueDateOptions();

    planSelectionForm: FormGroup;
    systemSelectionForm: FormGroup;
    moduleSelectionForm: FormGroup;

    searchTerm: string = '';
    systemSearchTerm: string = '';
    moduleSearchTerm: string = '';
    private searchTerms$ = new Subject<string>();
    activePlansCount: number = 0;
    inactivePlansCount: number = 0;
    totalFilteredPlansCount: number = 0;
    planCardsDataSource = new MatTableDataSource<IPlanForSelection>([]);
    planCardColumns: IColumnConfigTableResponsive[] = [
        { property: 'planDescription', class: '', display: 'DESCRIÇÃO DO ARQUIVO', field_type: FieldTypeEnum.LABEL },
        { property: 'systemLabel', class: '', display: 'SISTEMA', field_type: FieldTypeEnum.LABEL },
        { property: 'terminalQuantity', class: '', display: 'QTD TERMINAL', field_type: FieldTypeEnum.LABEL },
        { property: 'monthlyValue', class: '', display: 'VALOR MENSALIDADE', field_type: FieldTypeEnum.LABEL_CURRENCY },
        { property: 'adhesionFee', class: '', display: 'TAXA DE ADESÃO', field_type: FieldTypeEnum.LABEL_CURRENCY },
        { property: 'isDaysCountLabel', class: '', display: 'DIAS CORRIDOS', field_type: FieldTypeEnum.LABEL },
        { property: 'dueDateLabel', class: '', display: 'DATA DE VENCIMENTO', field_type: FieldTypeEnum.LABEL },
        { property: 'createdAtLabel', class: '', display: 'CRIADO EM', field_type: FieldTypeEnum.LABEL },
        { property: 'partnersLabel', class: '', display: 'PARCEIROS VINCULADOS', field_type: FieldTypeEnum.LABEL },
    ];

    activeFilter: 'active' | 'inactive' = 'active';

    @Input() selectedPartnerIds: string[] = [];

    constructor(
        private fb: FormBuilder,
        private planSelectionService: PlanSelectionService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private authService: AuthService,
        private toast: ToastService,
        public dialogRef: MatDialogRef<PlanSelectionWizardComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.isEditMode = !!(data && (data.preSelectedSystem || data.preSelectedPlan || data.preSelectedModules));
        if (data?.preSelectedMaxBranches !== undefined && data?.preSelectedMaxBranches !== null) {
            const n = Math.floor(Number(data.preSelectedMaxBranches));
            this.maxBranches = Number.isFinite(n) && n > 0 ? n : null;
        }
        // Inicializa selectedPartnerIds com os dados do diálogo
        if (data && data.selectedPartnerIds) {
            this.selectedPartnerIds = data.selectedPartnerIds;
        }
    }

    ngOnInit(): void {
        this.buildForms();
        this.setupSearchDebounce();
        this.loadInitialPlans();
    }

    ngAfterViewInit(): void {
        // Na edicao o wizard deve abrir SEMPRE no 1o passo (Plano), apenas com o
        // plano travado (sem permitir troca). Nao auto-avancamos mais para os
        // passos seguintes (variacoes/filiais).
        if (this.isEditMode && this.stepper) {
            setTimeout(() => {
                this.stepper.selectedIndex = 0;
                this.currentStep = PlanSelectionStep.SELECT_PLAN;
                this.cdr.markForCheck();
            }, 0);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private buildForms(): void {
        this.planSelectionForm = this.fb.group({
            selectedPlanId: [null, Validators.required]
        });
        this.systemSelectionForm = this.fb.group({
            selectedSystemId: [null, Validators.required]
        });
        this.moduleSelectionForm = this.fb.group({
            selectedVariationIds: [[]]
        });
    }

    private setupSearchDebounce(): void {
        this.searchTerms$
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe(term => {
                this.searchTerm = term;
                this.applyPlanFilters();
            });
    }

    onSearchInputChanged(event: any): void {
        const term = event.target.value;
        this.searchTerms$.next(term);
    }

    public loadInitialPlans(): void {
        this.isLoadingPlans = true;
        const serviceFilters: { systemType?: number; partnerIds?: string[] } = {};

        if (this.planFilters.systemType !== null && this.planFilters.systemType !== undefined) {
            serviceFilters.systemType = this.planFilters.systemType;
        }

        if (this.selectedPartnerIds && this.selectedPartnerIds.length > 0) {
            serviceFilters.partnerIds = this.selectedPartnerIds;
        }

        this.planSelectionService.getAvailablePlans(serviceFilters)
            .pipe(takeUntil(this.destroy$))
            .subscribe(plans => {
                this.allPlans = plans;

                // Atualiza os contadores antes de aplicar os filtros
                this.updateCounters();

                // Aplica os filtros iniciais
                this.applyPlanFilters();
                this.isLoadingPlans = false;

                if (this.isEditMode && this.data.preSelectedPlan) {
                    const preSelectedPlan = this.allPlans.find(p => p.id === this.data.preSelectedPlan.id);
                    if (preSelectedPlan) {
                        this.selectPlan(preSelectedPlan);
                    }
                }

                this.cdr.markForCheck();
            }, error => {
                console.error('[PlanSelectionWizard] Erro ao carregar planos:', error);
                this.isLoadingPlans = false;
                this.cdr.markForCheck();
            });
    }

    private loadSystemForSelectedPlan(): void {
        if (!this.selectedPlan) return;
        this.isLoadingSystems = true;
        this.availableSystems = [];
        this.selectedSystem = null;
        this.systemSelectionForm.reset();

        this.planSelectionService.getSystemForPlan(this.selectedPlan)
            .pipe(takeUntil(this.destroy$))
            .subscribe(system => {
                this.availableSystems = system ? [system] : [];
                this.isLoadingSystems = false;

                if (system) {
                    this.selectSystem(system);
                    if (this.isEditMode) {
                        this.loadVariationsForSelectedPlan();
                    }
                }

                this.cdr.markForCheck();
            });
    }

    private loadVariationsForSelectedPlan(): void {
        if (!this.selectedPlan) return;
        this.isLoadingModules = true;
        this.availableModules = [];
        this.availableVariations = [];
        this.selectedVariations = [];
        this.selectedModules = [];
        this.resetSummaryOverrides();
        this.moduleSelectionForm.reset({ selectedVariationIds: [] });

        const allVariations = Array.isArray(this.selectedPlan.variations) ? this.selectedPlan.variations : [];
        this.availableVariations = this.applySavedVariationValues(
            allVariations.filter((variation: IPlanVariationForSelection) =>
                String(variation?.status || 'ATIVO').toUpperCase() !== 'INATIVO'
            )
        );

        if (this.data.preSelectedModules?.length > 0) {
            const preSelectedModuleIds = new Set(
                this.data.preSelectedModules
                    .map((module: any) => String(module?.id || '').trim())
                    .filter(Boolean)
            );
            this.selectedVariations = this.availableVariations.filter((variation: IPlanVariationForSelection) =>
                (variation.modules || []).some((module: any) => preSelectedModuleIds.has(String(module?.id || '').trim()))
            );
        } else {
            // Novo comportamento: ao selecionar o plano, todas as variacoes ja vem marcadas.
            this.selectedVariations = [...this.availableVariations];
        }

        this.updateVariationFormValue();
        this.availableModules = this.deriveModulesFromSelectedVariations();
        this.isLoadingModules = false;
        this.initBranchRows();
        this.cdr.markForCheck();
    }

    // ------------------------------------------------------------------
    // Step 3 "Matriz & Filiais" (novo no Core)
    // ------------------------------------------------------------------

    private initBranchRows(): void {
        if (this.branchRowsInitialized) return;
        this.branchRowsInitialized = true;

        const source =
            this.data?.additionalBranches ??
            this.data?.customer?.additionalBranches ??
            this.data?.element?.additionalBranches ??
            [];

        let branches: any[] = [];
        if (Array.isArray(source)) {
            branches = source;
        } else if (typeof source === 'string' && source.trim()) {
            try {
                const parsed = JSON.parse(source);
                branches = Array.isArray(parsed) ? parsed : [];
            } catch {
                branches = [];
            }
        }

        const matrizRow: IBranchRow = {
            key: 'MATRIZ',
            title: 'Matriz',
            document: String(this.data?.customer?.document || this.data?.element?.document || '') || undefined,
            isMain: true,
            active: true,
        };

        const branchRows: IBranchRow[] = branches
            .filter((branch: any) => !(branch?.isMain ?? branch?.branchIsMain))
            .map((branch: any) => ({
                key: String(branch?.id || branch?.document || branch?.title || Math.random()),
                title: String(branch?.title || branch?.companyName || branch?.tradingName || 'Sem título'),
                document: String(branch?.document || ''),
                city: String(branch?.city || ''),
                state: String(branch?.state || ''),
                isMain: false,
                active: Boolean(branch?.active ?? true),
            }));

        this.branchRows = [matrizRow, ...branchRows];
        this.branchSelected = { MATRIZ: true };
        this.branchLimits = {};
        this.branchItemSelections = {};

        const savedCompositions: any[] = Array.isArray(this.data?.preSelectedBranchCompositions)
            ? this.data.preSelectedBranchCompositions
            : [];

        if (savedCompositions.length > 0) {
            this.applySavedBranchCompositions(savedCompositions);
        }
    }

    private applySavedBranchCompositions(savedCompositions: any[]): void {
        const normalizeDoc = (value: any): string => String(value ?? '').replace(/\D/g, '');
        const normalizeText = (value: any): string => String(value ?? '').trim().toLowerCase();

        // Se qualquer composição salva tiver uma lista de itens diferente do
        // conjunto global, a edição já foi personalizada por filial.
        const globalVariationIds = new Set(this.selectedVariations.map((v) => this.variationKey(v)));
        let anyDivergesFromGlobal = false;

        this.branchRows.forEach((row) => {
            const match = savedCompositions.find((composition: any) => {
                const compKey = String(composition?.branchKey ?? '').trim();
                const compDoc = normalizeDoc(composition?.branchDocument);
                const compTitle = normalizeText(composition?.branchTitle);
                if (compKey && compKey === row.key) return true;
                if (compDoc && row.document && compDoc === normalizeDoc(row.document)) return true;
                if (compTitle && compTitle === normalizeText(row.title)) return true;
                return false;
            });

            if (!match) return;

            this.branchSelected[row.key] = true;

            const selectedVariationIds = Array.isArray(match.selectedVariationIds)
                ? match.selectedVariationIds.map((id: any) => String(id))
                : [];
            const selectedModuleIds = Array.isArray(match.selectedModuleIds)
                ? match.selectedModuleIds.map((id: any) => String(id))
                : [];

            this.branchItemSelections[row.key] = { selectedVariationIds, selectedModuleIds };

            if (selectedVariationIds.length > 0 &&
                (selectedVariationIds.length !== globalVariationIds.size ||
                    selectedVariationIds.some((id: string) => !globalVariationIds.has(id)))) {
                anyDivergesFromGlobal = true;
            }

            this.branchLimits[row.key] = {
                terminalQuantity: typeof match.branchTerminalQuantity === 'number' ? match.branchTerminalQuantity : null,
                maxUsers: typeof match.branchMaxUsers === 'number' ? match.branchMaxUsers : null,
            };
        });

        this.perFilialMode = anyDivergesFromGlobal;
    }

    isMatrizRow(row: IBranchRow): boolean {
        return !!row?.isMain;
    }

    isBranchSelected(row: IBranchRow): boolean {
        return this.isMatrizRow(row) || !!this.branchSelected[row.key];
    }

    activeBranchesCount(): number {
        return this.branchRows.filter((row) => this.isBranchSelected(row)).length;
    }

    activeBranchRows(): IBranchRow[] {
        return this.branchRows.filter((row) => this.isBranchSelected(row));
    }

    /** Máx. de filiais atingido — bloqueia o toggle de novas filiais (enforcement real, não só aviso). */
    isBranchToggleDisabled(row: IBranchRow): boolean {
        if (this.isMatrizRow(row)) return true;
        if (this.maxBranches === null || this.maxBranches === undefined) return false;
        if (this.branchSelected[row.key]) return false;
        return this.activeBranchesCount() >= this.maxBranches;
    }

    toggleBranchSelection(row: IBranchRow): void {
        if (this.isMatrizRow(row)) return;
        if (this.isBranchToggleDisabled(row)) {
            this.toast.showWarn('Máx. de filiais atingido para este plano.');
            return;
        }
        this.branchSelected[row.key] = !this.branchSelected[row.key];
        if (this.branchSelected[row.key] && this.perFilialMode && !this.branchItemSelections[row.key]) {
            this.branchItemSelections[row.key] = this.cloneGlobalCompositionSelection();
        }
        this.cdr.markForCheck();
    }

    /** Pill de rodapé "Filiais no plano: N" — fica "bad" se ultrapassar o máximo. */
    branchesLimitExceeded(): boolean {
        return this.maxBranches !== null && this.maxBranches !== undefined && this.activeBranchesCount() > this.maxBranches;
    }

    setBranchesMaxLimit(event: any): void {
        this.setMaxBranches(event);
    }

    setPerFilialMode(personalize: boolean): void {
        this.perFilialMode = personalize;
        if (personalize) {
            this.activeBranchRows().forEach((row) => {
                if (!this.branchItemSelections[row.key]) {
                    this.branchItemSelections[row.key] = this.cloneGlobalCompositionSelection();
                }
            });
        }
        this.cdr.markForCheck();
    }

    private cloneGlobalCompositionSelection(): { selectedVariationIds: string[]; selectedModuleIds: string[] } {
        return {
            selectedVariationIds: this.selectedVariations.map((v) => this.variationKey(v)),
            selectedModuleIds: (this.selectedModules || []).map((m) => String(m.id || '')),
        };
    }

    /** Todos os itens (variações + extras) disponíveis para inclusão/exclusão por filial. */
    getCompositionItemsForBranchEditor(): Array<{ key: string; title: string; kind: 'variation' | 'module'; monthlyValue: number }> {
        const variationItems = this.selectedVariations.map((v) => ({
            key: this.variationKey(v),
            title: String(v?.nome || v?.codigo || 'Variação'),
            kind: 'variation' as const,
            monthlyValue: Number(v?.valorMensalidade || 0),
        }));
        const moduleItems = (this.selectedModules || []).map((m) => ({
            key: String(m.id || ''),
            title: String(m?.name || 'Módulo extra'),
            kind: 'module' as const,
            monthlyValue: Number(m?.accessValue || 0),
        }));
        return [...variationItems, ...moduleItems];
    }

    isBranchItemIncluded(row: IBranchRow, item: { key: string; kind: 'variation' | 'module' }): boolean {
        if (!this.perFilialMode) return true;
        const sel = this.branchItemSelections[row.key];
        if (!sel) return true;
        return item.kind === 'variation'
            ? sel.selectedVariationIds.includes(item.key)
            : sel.selectedModuleIds.includes(item.key);
    }

    toggleBranchItemInclusion(row: IBranchRow, item: { key: string; kind: 'variation' | 'module' }): void {
        if (!this.perFilialMode) return;
        if (!this.branchItemSelections[row.key]) {
            this.branchItemSelections[row.key] = this.cloneGlobalCompositionSelection();
        }
        const sel = this.branchItemSelections[row.key];
        const list = item.kind === 'variation' ? sel.selectedVariationIds : sel.selectedModuleIds;
        const idx = list.indexOf(item.key);
        if (idx >= 0) {
            list.splice(idx, 1);
        } else {
            list.push(item.key);
        }
        this.cdr.markForCheck();
    }

    /** Valor mensal calculado pela composição efetiva de uma filial (Step 3, coluna de valor). */
    getBranchMonthlyValue(row: IBranchRow): number {
        const items = this.getCompositionItemsForBranchEditor();
        if (!this.perFilialMode || !this.branchItemSelections[row.key]) {
            return items.reduce((sum, item) => sum + item.monthlyValue, 0);
        }
        return items
            .filter((item) => this.isBranchItemIncluded(row, item))
            .reduce((sum, item) => sum + item.monthlyValue, 0);
    }

    // ------------------------------------------------------------------
    // Step 4 "Limites por filial" (novo no Core)
    // ------------------------------------------------------------------

    getBranchTerminalQuantity(row: IBranchRow): number | null {
        return this.branchLimits[row.key]?.terminalQuantity ?? null;
    }

    getBranchMaxUsers(row: IBranchRow): number | null {
        return this.branchLimits[row.key]?.maxUsers ?? null;
    }

    setBranchTerminalQuantity(row: IBranchRow, event: any): void {
        const raw = event?.target?.value ?? event;
        const parsed = raw === '' || raw === null || raw === undefined ? null : Math.floor(this.toNumber(raw));
        if (!this.branchLimits[row.key]) this.branchLimits[row.key] = { terminalQuantity: null, maxUsers: null };
        this.branchLimits[row.key].terminalQuantity = Number.isFinite(parsed as number) && (parsed as number) >= 0 ? parsed : null;
        this.cdr.markForCheck();
    }

    setBranchMaxUsers(row: IBranchRow, event: any): void {
        const raw = event?.target?.value ?? event;
        const parsed = raw === '' || raw === null || raw === undefined ? null : Math.floor(this.toNumber(raw));
        if (!this.branchLimits[row.key]) this.branchLimits[row.key] = { terminalQuantity: null, maxUsers: null };
        this.branchLimits[row.key].maxUsers = Number.isFinite(parsed as number) && (parsed as number) >= 0 ? parsed : null;
        this.cdr.markForCheck();
    }

    getBranchTerminalsTotal(): number {
        return this.activeBranchRows().reduce((sum, row) => sum + (this.getBranchTerminalQuantity(row) || 0), 0);
    }

    getBranchUsersTotal(): number {
        return this.activeBranchRows().reduce((sum, row) => sum + (this.getBranchMaxUsers(row) || 0), 0);
    }

    /** Soma de terminais por filial excede o plano — visual apenas (não bloqueia o avanço, igual ao protótipo). */
    branchTerminalsExceedPlan(): boolean {
        const planTerm = this.planTerminalQuantity ?? this.selectedPlan?.terminalQuantity ?? 0;
        return planTerm > 0 && this.getBranchTerminalsTotal() > planTerm;
    }

    // Na edicao, reaplica sobre as variacoes do plano os valores que o cliente
    // ja havia customizado (mensalidade/adesao/custo por variacao), para que
    // reabrir o wizard mostre o valor salvo e nao o default do plano.
    private applySavedVariationValues(variations: IPlanVariationForSelection[]): IPlanVariationForSelection[] {
        const saved = Array.isArray(this.data?.preSelectedVariationValues)
            ? this.data.preSelectedVariationValues
            : [];
        if (saved.length === 0) return variations;

        const byId = new Map<string, any>();
        saved.forEach((value: any) => {
            const id = String(value?.id || value?.variationId || value?.planVariationId || '').trim();
            if (id) byId.set(id, value);
        });

        return variations.map((variation) => {
            const id = String(variation?.id || variation?.codigo || '').trim();
            const match = byId.get(id);
            if (!match) return variation;
            return {
                ...variation,
                valorMensalidade: match?.valorMensalidade ?? match?.monthlyValue ?? variation?.valorMensalidade,
                adesaoValor: match?.adesaoValor ?? match?.adhesionValue ?? variation?.adesaoValor,
                custo: (match as any)?.custo ?? (match as any)?.cost ?? (variation as any)?.custo,
            } as IPlanVariationForSelection;
        });
    }

    selectPlan(plan: IPlanForSelection): void {
        if (!plan.isActive) return;
        // Regra de negocio: ao editar, o plano NAO pode ser trocado — apenas
        // variacoes, modulos e filiais. A pre-selecao (selectedPlan ainda nulo)
        // passa; tentativas de mudar para outro plano sao bloqueadas.
        if (this.isEditMode && this.selectedPlan && plan.id !== this.selectedPlan.id) {
            this.toast.showWarn(
                'O plano não pode ser alterado na edição. Ajuste apenas variações, módulos e filiais.'
            );
            return;
        }
        this.selectedPlan = plan;
        this.planSelectionForm.patchValue({ selectedPlanId: plan.id });
        // Step 1 "Terminais do plano": pré-preenchido do catálogo, editável.
        // Na edição, prevalece o valor já salvo no customer_plan (se houver).
        const savedTerminalQuantity = this.toNumber(this.data?.preSelectedPlan?.terminalQuantity);
        this.planTerminalQuantity = savedTerminalQuantity > 0 ? savedTerminalQuantity : (plan.terminalQuantity ?? null);
        this.loadSystemForSelectedPlan();
        this.cdr.markForCheck();
    }

    setPlanTerminalQuantity(event: any): void {
        const raw = event?.target?.value ?? event;
        if (raw === '' || raw === null || raw === undefined) {
            this.planTerminalQuantity = null;
            this.cdr.markForCheck();
            return;
        }
        const parsed = Math.floor(this.toNumber(raw));
        this.planTerminalQuantity = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
        this.cdr.markForCheck();
    }

    selectSystem(system: ISystemForSelection): void {
        if (!system.isActive) return;
        this.selectedSystem = system;
        this.systemSelectionForm.patchValue({ selectedSystemId: system.id });
        this.cdr.markForCheck();
    }

    isVariationSelected(variationId: string): boolean {
        return this.selectedVariations.some((variation) => String(variation.id || variation.codigo || '') === String(variationId));
    }

    getVariationModuleNames(variation: IPlanVariationForSelection): string {
        const modules = Array.isArray(variation?.modules) ? variation.modules : [];
        const names = modules
            .map((module: any) => String(module?.name || '').trim())
            .filter((name: string) => !!name);

        return names.length > 0 ? names.join(', ') : 'N/A';
    }

    toggleVariationSelection(variation: IPlanVariationForSelection): void {
        const key = String(variation.id || variation.codigo || '');
        if (!key) return;

        if (this.isVariationSelected(key)) {
            this.removeVariationSelection(variation);
        } else {
            this.addVariationSelection(variation);
        }
    }

    addVariationSelection(variation: IPlanVariationForSelection): void {
        this.selectedVariations.push(variation);
        this.updateVariationFormValue();
        this.cdr.markForCheck();
    }

    removeVariationSelection(variation: IPlanVariationForSelection): void {
        const key = String(variation.id || variation.codigo || '');
        this.selectedVariations = this.selectedVariations.filter((item) => String(item.id || item.codigo || '') !== key);
        this.updateVariationFormValue();
        this.cdr.markForCheck();
    }

    removeExtraModuleSelection(module: ISystemModuleForSelection): void {
        const moduleId = String(module?.id || '').trim();
        if (!moduleId) return;
        this.selectedModules = (this.selectedModules || []).filter(
            (item) => String(item?.id || '').trim() !== moduleId
        );
        this.cdr.markForCheck();
    }

    getSelectedExtraModulesForCards(): ISystemModuleForSelection[] {
        return Array.isArray(this.selectedModules) ? this.selectedModules : [];
    }

    selectAllAvailableVariations(): void {
        if (!Array.isArray(this.availableVariations) || this.availableVariations.length === 0) {
            return;
        }

        const selectedKeys = new Set(
            this.selectedVariations.map((variation) => String(variation?.id || variation?.codigo || '').trim())
        );

        const toAdd = this.availableVariations.filter((variation) => {
            const key = String(variation?.id || variation?.codigo || '').trim();
            return !!key && !selectedKeys.has(key);
        });

        if (toAdd.length === 0) {
            return;
        }

        this.selectedVariations = [...this.selectedVariations, ...toAdd];
        this.updateVariationFormValue();
        this.cdr.markForCheck();
    }

    openModulesByPlanSolutionModal(): void {
        if (!this.selectedSystem?.id) {
            return;
        }

        const variations = Array.isArray(this.availableVariations) ? this.availableVariations : [];
        const firstVariation = variations[0];
        const solutionName = String(
            variations.find((variation) => String(variation?.solucao || '').trim())?.solucao || this.selectedPlan?.systemName || ''
        ).trim();

        this.planSelectionService.getAvailableModules(this.selectedSystem.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((modules) => {
                const variationModuleIds = new Set(
                    this.deriveModulesFromSelectedVariations().map((module) => String(module.id || '').trim())
                );
                const selectableModules = (Array.isArray(modules) ? modules : []).filter((module) => {
                    const moduleId = String(module?.id || '').trim();
                    return !!moduleId && !variationModuleIds.has(moduleId);
                });

                const ref = this.dialog.open(PlanSolutionModulesDialogComponent, {
                    width: '900px',
                    maxWidth: '95vw',
                    maxHeight: '85vh',
                    data: {
                        solutionName: solutionName || 'Não definida',
                        modules: selectableModules,
                        preSelectedModuleIds: this.selectedModules.map((module) => String(module.id || '').trim()),
                        planName: String(this.selectedPlan?.systemName || this.selectedPlan?.planDescription || 'Plano').trim(),
                        variationCode: String(firstVariation?.codigo || '--').trim() || '--',
                        productTypeLabel: this.getVariationTypeDescription(firstVariation || {} as IPlanVariationForSelection),
                        statusLabel: String(firstVariation?.status || 'ATIVO').trim() || 'ATIVO',
                    },
                });

                ref.afterClosed()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe((selected: ISystemModuleForSelection[] | undefined) => {
                        if (!Array.isArray(selected)) {
                            return;
                        }
                        this.selectedModules = selected;
                        this.cdr.markForCheck();
                    });
            });
    }

    private updateVariationFormValue(): void {
        const variationIds = this.selectedVariations.map((variation) => String(variation.id || variation.codigo || ''));
        this.moduleSelectionForm.patchValue({ selectedVariationIds: variationIds });

        this.availableModules = this.deriveModulesFromSelectedVariations();
        const variationModuleIds = new Set(this.availableModules.map((module) => String(module.id || '').trim()));
        this.selectedModules = (this.selectedModules || []).filter((module) => {
            const moduleId = String(module?.id || '').trim();
            return !!moduleId && !variationModuleIds.has(moduleId);
        });
    }

    private deriveModulesFromSelectedVariations(): ISystemModuleForSelection[] {
        const moduleMap = new Map<string, ISystemModuleForSelection>();
        this.selectedVariations.forEach((variation: IPlanVariationForSelection) => {
            const modules = Array.isArray(variation?.modules) ? variation.modules : [];
            modules.forEach((module: any) => {
                const id = String(module?.id || '').trim();
                if (!id || moduleMap.has(id)) return;
                moduleMap.set(id, {
                    id,
                    name: String(module?.name || 'Módulo').trim() || 'Módulo',
                    isActive: true,
                });
            });
        });
        return Array.from(moduleMap.values());
    }

    variationKey(variation: IPlanVariationForSelection): string {
        return String(variation?.id || variation?.codigo || '').trim();
    }

    handleSystemConfirmation(): void {
        if (!this.selectedPlan) {
            console.warn('Tentativa de confirmar sem plano selecionado.');
            return;
        }

        if (this.isLoadingSystems) {
            return;
        }

        if (!this.selectedSystem) {
            console.error('Erro: Sistema não foi carregado para o plano selecionado.');
            return;
        }

        this.isLoadingModules = true;
        this.promptedForModule = true;

        this.loadVariationsForSelectedPlan();
        this.stepper.selectedIndex = this.availableVariations.length > 0 ? 1 : 2;
        this.isLoadingModules = false;
        this.cdr.markForCheck();
    }

    skipModuleStep(): void {
        const summaryStepIndex = PlanSelectionStep.SUMMARY;
        this.selectedVariations = [];
        this.selectedModules = [];
        this.selectedModule = null;
        this.moduleSelectionForm.reset({ selectedVariationIds: [] });
        this.stepper.selectedIndex = summaryStepIndex;
        this.cdr.markForCheck();
    }

    resetWizard(): void {
        this.selectedPlan = null;
        this.selectedSystem = null;
        this.selectedModule = null;
        this.selectedVariations = [];
        this.selectedModules = [];
        this.resetSummaryOverrides();
        this.maxBranches = null;
        this.planTerminalQuantity = null;
        this.branchRows = [];
        this.branchSelected = {};
        this.perFilialMode = false;
        this.branchItemSelections = {};
        this.branchLimits = {};
        this.branchRowsInitialized = false;
        this.updateReason = '';
        this.promptedForModule = false;
        this.planSelectionForm.reset();
        this.systemSelectionForm.reset();
        this.moduleSelectionForm.reset({ selectedVariationIds: [] });
        this.stepper.reset();
        this.currentStep = PlanSelectionStep.SELECT_PLAN;
        this.loadInitialPlans();
        this.cdr.markForCheck();
    }

    /** Step 5: "Motivo da alteração" é obrigatório só na edição (Core). */
    get isUpdateReasonRequired(): boolean {
        return this.isEditMode;
    }

    get isUpdateReasonMissing(): boolean {
        return this.isUpdateReasonRequired && !this.updateReason.trim();
    }

    setUpdateReason(event: any): void {
        this.updateReason = String(event?.target?.value ?? event ?? '');
        this.cdr.markForCheck();
    }

    private buildBranchCompositions(): IBranchComposition[] {
        return this.activeBranchRows().map((row) => {
            const perFilial = this.perFilialMode ? this.branchItemSelections[row.key] : null;
            const limits = this.branchLimits[row.key] || { terminalQuantity: null, maxUsers: null };

            return {
                branchKey: row.key,
                branchTitle: row.title,
                branchDocument: row.document || null,
                selectedVariationIds: perFilial
                    ? perFilial.selectedVariationIds
                    : this.selectedVariations.map((v) => this.variationKey(v)),
                selectedModuleIds: perFilial
                    ? perFilial.selectedModuleIds
                    : (this.selectedModules || []).map((m) => String(m.id || '')),
                branchTerminalQuantity: typeof limits.terminalQuantity === 'number' ? limits.terminalQuantity : null,
                branchMaxUsers: typeof limits.maxUsers === 'number' ? limits.maxUsers : null,
            };
        });
    }

    finishSelection(): void {
        if (!this.selectedPlan || !this.selectedSystem) {
            console.error('Tentativa de finalizar seleção sem plano ou sistema selecionado');
            return;
        }

        if (this.isUpdateReasonMissing) {
            this.toast.showWarn('Informe o motivo da alteração para concluir a edição do plano.');
            return;
        }

        if (this.availableVariations && this.availableVariations.length > 0 && this.selectedVariations.length === 0) {
            this.selectedVariations = [this.availableVariations[0]];
            this.updateVariationFormValue();
        }

        const result = {
            plan: {
                ...this.selectedPlan,
                terminalQuantity: this.planTerminalQuantity ?? this.selectedPlan.terminalQuantity,
                isDaysCount: !!this.selectedPlan.isDaysCount
            },
            system: this.selectedSystem,
            modules: this.selectedModules.length > 0 ? this.selectedModules : undefined,
            variations: this.selectedVariations.length > 0 ? this.selectedVariations : undefined,
            branchCompositions: this.buildBranchCompositions(),
            maxBranches: this.maxBranches,
            updateReason: this.isEditMode ? this.updateReason.trim() : undefined,
            summary: {
                dueDate: this.getSummaryDueDate(),
                monthlyValue: this.getSummaryMonthlyValue(),
                adhesionFee: this.getSummaryAdhesionFee(),
                cost: this.getSummaryCost(),
                totalValue: this.getTotalValue(),
            } as IPlanSelectionSummary
        };

        this.selectionFinished.emit(result);
        this.dialogRef.close(result);
    }

    cancelSelection(): void {
        if ((this.dialog as any).openDialogs && (this.dialog as any).openDialogs.length > 0) {
            const currentDialogRef = (this.dialog as any).openDialogs[(this.dialog as any).openDialogs.length - 1];
            if (currentDialogRef) {
                currentDialogRef.close();
            }
        }
    }

    applyPlanFilters(): void {
        if (this.allPlans.length === 0) {
            this.planCardsDataSource.data = [];
            return;
        }

        try {
            let filtered = this.allPlans.filter(plan => {
                if (!plan) return false;

                const matchesSystemType = !this.planFilters.systemType ||
                    plan.type === this.planFilters.systemType;

                const matchesDueDate = !this.planFilters.dueDate ||
                    (plan.dueDate !== null && plan.dueDate === this.planFilters.dueDate);

                let matchesSearchTerm = true;
                if (this.searchTerm && this.searchTerm.trim() !== '') {
                    const term = this.searchTerm.toLowerCase().trim();
                    matchesSearchTerm = (
                        (plan.systemName && plan.systemName.toLowerCase().includes(term)) ||
                        (this.getSystemTypeDescription(plan.type).toLowerCase().includes(term)) ||
                        (String(plan.terminalQuantity).includes(term))
                    );
                }

                return matchesSystemType && matchesSearchTerm && matchesDueDate;
            });

            this.filteredPlans = JSON.parse(JSON.stringify(filtered));
            this.sortPlans(this.filteredPlans);
            this.refreshPlanCards();

            this.updateCounters();

            this.cdr.detectChanges();
        } catch (error) {
            console.error('[PlanSelectionWizard] Erro ao filtrar planos:', error);
            this.filteredPlans = this.allPlans.slice();
            this.sortPlans(this.filteredPlans);
            this.refreshPlanCards();
            this.updateCounters();
            this.cdr.detectChanges();
        }
    }

    private refreshPlanCards(): void {
        // Regra de negocio: ao EDITAR, o plano nao pode ser trocado. Mostra
        // APENAS o plano atual (buscado em allPlans para aparecer mesmo se um
        // filtro o excluiria), impedindo a selecao de qualquer outro.
        const source =
            this.isEditMode && this.data?.preSelectedPlan
                ? this.allPlans.filter((p) => p.id === this.data.preSelectedPlan.id)
                : this.filteredPlans;
        this.planCardsDataSource.data = source.map((plan) => ({
            ...plan,
            monthlyValue: this.getPlanCardMonthlyValue(plan),
            adhesionFee: this.getPlanCardAdhesionFee(plan),
            typeLabel: this.getSystemTypeDescription(Number(plan?.type || 0)),
            dueDateLabel: plan?.dueDate && plan.dueDate > 0 ? String(plan.dueDate) : 'Não definido',
            planDescription: String((plan as any)?.planDescription || (plan as any)?.description || '').trim() || '--',
            systemLabel: String((plan as any)?.system || (plan as any)?.systemName || '').trim() || '--',
            partnersLabel: this.getPartnersLabel(plan),
            isDaysCountLabel: (plan as any)?.isDaysCount ? 'Sim' : 'Não',
            createdAtLabel: this.formatDateTime((plan as any)?.createdAt)
        } as IPlanForSelection));
    }

    private getPlanCardMonthlyValue(plan: IPlanForSelection): number {
        const variations = Array.isArray((plan as any)?.variations) ? (plan as any).variations : [];
        if (variations.length === 0) return this.toNumber((plan as any)?.monthlyValue);
        return variations.reduce((acc: number, variation: any) => {
            const rawPayload = this.normalizeRawPayload(variation?.raw_payload || variation?.rawPayload);
            const value = this.toNumber(
                variation?.valorMensalidade ??
                variation?.monthlyValue ??
                variation?.value ??
                rawPayload?.valorMensalidade ??
                rawPayload?.monthlyValue ??
                rawPayload?.valorAcesso
            );
            return acc + value;
        }, 0);
    }

    private getPlanCardAdhesionFee(plan: IPlanForSelection): number {
        const variations = Array.isArray((plan as any)?.variations) ? (plan as any).variations : [];
        if (variations.length === 0) return this.toNumber((plan as any)?.adhesionFee);
        return variations.reduce((acc: number, variation: any) => {
            const rawPayload = this.normalizeRawPayload(variation?.raw_payload || variation?.rawPayload);
            const value = this.toNumber(
                variation?.adesaoValor ??
                variation?.adhesionFee ??
                rawPayload?.adesaoValor ??
                rawPayload?.adhesionFee
            );
            return acc + value;
        }, 0);
    }

    private normalizeRawPayload(payload: any): any {
        if (!payload) return null;
        if (typeof payload === 'string') {
            try {
                return JSON.parse(payload);
            } catch {
                return null;
            }
        }
        return payload;
    }

    private toNumber(value: any): number {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        if (typeof value === 'string') {
            const cleaned = value.replace(/[R$\s]/g, '');
            const hasComma = cleaned.includes(',');
            const hasDot = cleaned.includes('.');
            let normalized = cleaned;

            // pt-BR: 1.234,56 -> 1234.56
            if (hasComma && hasDot) {
                normalized = cleaned.replace(/\./g, '').replace(',', '.');
            } else if (hasComma) {
                // 123,45 -> 123.45
                normalized = cleaned.replace(',', '.');
            } else {
                // 180.00 stays 180.00
                normalized = cleaned;
            }

            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : 0;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private getPartnersLabel(plan: IPlanForSelection): string {
        const partners = (plan as any)?.partners;
        if (!Array.isArray(partners) || partners.length === 0) return '--';
        const names = partners
            .map((p: any) => String(p?.companyName || p?.name || '').trim())
            .filter(Boolean);
        return names.length > 0 ? names.join(', ') : '--';
    }

    private formatDateTime(value: any): string {
        if (!value) return '--';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '--';
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    onPlanCardSelectionChange(event: { items?: IPlanForSelection[] }): void {
        const selected = event?.items?.[0];
        if (!selected) return;
        this.selectPlan(selected);
    }

    filterSystems(): void {
        if (!this.systemSearchTerm) return;
        const term = this.systemSearchTerm.toLowerCase();
        this.availableSystems = this.availableSystems.filter(system =>
            system.name.toLowerCase().includes(term) ||
            this.getSystemTypeDescription(system.type).toLowerCase().includes(term)
        );
        this.cdr.markForCheck();
    }

    filterModules(): void {
        const term = String(this.moduleSearchTerm || '').toLowerCase().trim();
        const baseVariations = Array.isArray(this.selectedPlan?.variations) ? this.selectedPlan!.variations! : [];

        if (!term) {
            this.availableVariations = baseVariations.filter((variation: IPlanVariationForSelection) =>
                String(variation?.status || 'ATIVO').toUpperCase() !== 'INATIVO'
            );
            this.cdr.markForCheck();
            return;
        }

        this.availableVariations = baseVariations.filter((variation: IPlanVariationForSelection) => {
            const isActive = String(variation?.status || 'ATIVO').toUpperCase() !== 'INATIVO';
            if (!isActive) return false;
            const searchable = [
                String(variation?.nome || ''),
                String(variation?.codigo || ''),
                String(variation?.solucao || ''),
            ].join(' ').toLowerCase();
            return searchable.includes(term);
        });
        this.cdr.markForCheck();
    }

    getTotalValue(): number {
        // A adesao e taxa unica e NAO compoe o total (valor recorrente/mensal).
        return this.getSummaryMonthlyValue();
    }

    getSummaryVariations(): IPlanVariationForSelection[] {
        return this.selectedVariations || [];
    }

    getSummaryCompositionItems(): Array<{
        kind: 'variation' | 'module';
        ref: any;
        title: string;
        system: string;
        module: string;
        monthlyValue: number;
        adhesionValue: number;
        costValue: number;
    }> {
        const variationItems = (this.selectedVariations || []).map((variation) => ({
            kind: 'variation' as const,
            ref: variation,
            title: String(variation?.nome || variation?.codigo || 'Variação'),
            system: String(variation?.solucao || this.selectedSystem?.name || 'N/A'),
            module: this.getVariationModuleNames(variation),
            monthlyValue: Number(variation?.valorMensalidade || 0),
            adhesionValue: Number(variation?.adesaoValor || 0),
            costValue: this.getVariationCost(variation),
        }));

        const moduleItems = (this.selectedModules || []).map((module) => ({
            kind: 'module' as const,
            ref: module,
            title: String(module?.name || 'Módulo'),
            system: String(this.selectedPlan?.systemName || this.selectedSystem?.name || 'N/A'),
            module: String(module?.name || 'N/A'),
            monthlyValue: Number(module?.accessValue || 0),
            adhesionValue: Number(module?.adhesionValue || 0),
            costValue: Number(module?.cost || 0),
        }));

        return [...variationItems, ...moduleItems];
    }

    // Edicao por variacao/modulo no passo de Resumo. Escreve direto no objeto
    // de origem (selectedVariations/selectedModules) e limpa o override do total
    // correspondente para o total voltar a refletir a soma das linhas.
    setCompositionMonthlyValue(item: any, event: any): void {
        const value = this.parseEditableSummaryValue(event);
        if (item?.kind === 'module') {
            item.ref.accessValue = value ?? 0;
        } else if (item?.ref) {
            item.ref.valorMensalidade = value ?? 0;
        }
        this.summaryMonthlyValueOverride = null;
        this.cdr.markForCheck();
    }

    setCompositionAdhesionValue(item: any, event: any): void {
        const value = this.parseEditableSummaryValue(event);
        if (item?.kind === 'module') {
            item.ref.adhesionValue = value ?? 0;
        } else if (item?.ref) {
            item.ref.adesaoValor = value ?? 0;
        }
        this.summaryAdhesionFeeOverride = null;
        this.cdr.markForCheck();
    }

    setCompositionCost(item: any, event: any): void {
        const value = this.parseEditableSummaryValue(event);
        if (item?.kind === 'module') {
            item.ref.cost = value ?? 0;
        } else if (item?.ref) {
            item.ref.custo = value ?? 0;
            item.ref.cost = value ?? 0;
        }
        this.summaryCostOverride = null;
        this.cdr.markForCheck();
    }

    getSummarySystemNames(): string[] {
        const names = new Set<string>();
        this.getSummaryVariations().forEach((variation: IPlanVariationForSelection) => {
            const name = String(variation?.solucao || '').trim();
            if (name) names.add(name);
        });
        return Array.from(names);
    }

    getSummaryDueDate(): number | null {
        const variations = this.getSummaryVariations();
        if (variations.length === 0) {
            return this.selectedPlan?.dueDate ?? null;
        }

        const first = variations.find((variation) => variation?.vencimento !== null && variation?.vencimento !== undefined);
        return first?.vencimento ?? this.selectedPlan?.dueDate ?? null;
    }

    getSummaryMonthlyValue(): number {
        if (this.summaryMonthlyValueOverride !== null) {
            return this.summaryMonthlyValueOverride;
        }

        const variations = this.getSummaryVariations();
        const variationsTotal = variations.reduce((acc: number, variation: IPlanVariationForSelection) => {
            return acc + Number(variation?.valorMensalidade || 0);
        }, 0);

        const modulesTotal = (this.selectedModules || []).reduce((acc: number, module: ISystemModuleForSelection) => {
            return acc + Number(module?.accessValue || 0);
        }, 0);

        if (variations.length === 0 && modulesTotal === 0) {
            return Number(this.selectedPlan?.monthlyValue || 0);
        }

        return variationsTotal + modulesTotal;
    }

    getSummaryAdhesionFee(): number {
        if (this.summaryAdhesionFeeOverride !== null) {
            return this.summaryAdhesionFeeOverride;
        }

        const variations = this.getSummaryVariations();
        const variationsTotal = variations.reduce((acc: number, variation: IPlanVariationForSelection) => {
            return acc + Number(variation?.adesaoValor || 0);
        }, 0);

        const modulesTotal = (this.selectedModules || []).reduce((acc: number, module: ISystemModuleForSelection) => {
            return acc + Number(module?.adhesionValue || 0);
        }, 0);

        if (variations.length === 0 && modulesTotal === 0) {
            return Number(this.selectedPlan?.adhesionFee || 0);
        }

        return variationsTotal + modulesTotal;
    }

    setSummaryMonthlyValue(event: any): void {
        this.summaryMonthlyValueOverride = this.parseEditableSummaryValue(event);
        this.cdr.markForCheck();
    }

    setSummaryAdhesionFee(event: any): void {
        this.summaryAdhesionFeeOverride = this.parseEditableSummaryValue(event);
        this.cdr.markForCheck();
    }

    setSummaryCost(event: any): void {
        this.summaryCostOverride = this.parseEditableSummaryValue(event);
        this.cdr.markForCheck();
    }

    setMaxBranches(event: any): void {
        const raw = event?.target?.value ?? event;
        if (raw === '' || raw === null || raw === undefined) {
            this.maxBranches = null;
        } else {
            const parsed = Math.floor(this.toNumber(raw));
            this.maxBranches = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
        }
        this.cdr.markForCheck();
    }

    private parseEditableSummaryValue(event: any): number | null {
        const raw = event?.target?.value ?? event;
        if (raw === '' || raw === null || raw === undefined) return null;
        const parsed = this.toNumber(raw);
        return Number.isFinite(parsed) ? parsed : null;
    }

    private resetSummaryOverrides(): void {
        this.summaryMonthlyValueOverride = null;
        this.summaryAdhesionFeeOverride = null;
        this.summaryCostOverride = null;
    }

    getVariationCost(variation: IPlanVariationForSelection): number {
        return Number((variation as any)?.custo ?? (variation as any)?.cost ?? 0);
    }

    getVariationTypeDescription(variation: IPlanVariationForSelection): string {
        const variationType = Number((variation as any)?.tipoSolucao);
        if (!Number.isNaN(variationType) && variationType > 0) {
            return this.getSystemTypeDescription(variationType);
        }

        if (this.selectedPlan && typeof this.selectedPlan.type === 'number') {
            return this.getSystemTypeDescription(this.selectedPlan.type);
        }

        return '--';
    }

    getSummaryCost(): number {
        if (this.summaryCostOverride !== null) {
            return this.summaryCostOverride;
        }

        const variations = this.getSummaryVariations();
        const variationsTotal = variations.reduce((acc: number, variation: IPlanVariationForSelection) => {
            return acc + this.getVariationCost(variation);
        }, 0);

        const modulesTotal = (this.selectedModules || []).reduce((acc: number, module: ISystemModuleForSelection) => {
            return acc + Number(module?.cost || 0);
        }, 0);

        if (variations.length === 0 && modulesTotal === 0) {
            return Number((this.selectedPlan as any)?.cost || 0);
        }

        return variationsTotal + modulesTotal;
    }

    get isPartnerUser(): boolean {
        return this.authService.isPartner();
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.planFilters = { systemType: null, dueDate: null };
        this.searchTerms$.next('');

        this.applyPlanFilters();
        this.cdr.detectChanges();
    }

    private sortPlans(plans: IPlanForSelection[]): void {
        plans.sort((a, b) => {
            if (a.type === 1 && b.type !== 1) return -1;
            if (a.type !== 1 && b.type === 1) return 1;

            if (a.type === 2 && b.type !== 2) return -1;
            if (a.type !== 2 && b.type === 2) return 1;

            if (a.type === 3 && b.type !== 3) return -1;
            if (a.type !== 3 && b.type === 3) return 1;

            if (a.isActive && !b.isActive) return -1;
            if (!a.isActive && b.isActive) return 1;

            if (a.terminalQuantity !== b.terminalQuantity) {
                return a.terminalQuantity - b.terminalQuantity;
            }

            if (a.systemName && b.systemName) {
                return a.systemName.localeCompare(b.systemName);
            }

            return 0;
        });
    }

    private updateCounters(): void {
        // Sempre calcula baseado em todos os planos (allPlans), não nos filtrados
        this.activePlansCount = this.allPlans.filter(plan => plan.isActive).length;
        this.inactivePlansCount = this.allPlans.filter(plan => !plan.isActive).length;

        // Total de planos na lista filtrada atual
        this.totalFilteredPlansCount = this.filteredPlans.length;
    }

    onSearchNoResults(): void {
        this.searchTerm = '';
        this.searchTerms$.next('');
        this.planFilters = { systemType: null, dueDate: null };

        this.filteredPlans = [];
        this.cdr.detectChanges();

        this.loadInitialPlans();
    }

    toggleActiveFilter(filter: 'active' | 'inactive'): void {
        this.activeFilter = filter;
        this.applyPlanFilters();
    }
}
